import React, { useState } from "react";
import { assignInvoice } from "@/lib/apiClient";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { UserCheck, Loader2, X, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const AssignCard = ({ inv, onChanged }) => {
  const { user, users } = useApp();
  const [assignee, setAssignee] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const decided = inv.status === "approved" || inv.status === "rejected";

  const run = async (target) => {
    setBusy(true);
    try {
      const updated = await assignInvoice(inv.id, target, user, note);
      setAssignee("");
      setNote("");
      toast.success(target ? `Handed off to ${target}` : "Assignment cleared");
      onChanged(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not assign");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="assign-card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <UserCheck className="h-4 w-4 text-primary" /> Hand-off
        </div>
        {inv.assigned_to ? (
          <span data-testid="assigned-badge" className="inline-flex items-center gap-1.5 rounded-md bg-primary/15 px-2 py-0.5 text-xs text-primary">
            Assigned to <span className="font-medium">{inv.assigned_to === user ? "you" : inv.assigned_to}</span>
            {!decided && (
              <button data-testid="unassign-btn" onClick={() => run(null)} disabled={busy} className="ml-0.5 hover:text-foreground" aria-label="Unassign">
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground" data-testid="unassigned-label">Unassigned</span>
        )}
      </div>
      {inv.assigned_to && (
        <p className="text-xs text-muted-foreground font-mono mb-3">
          by {inv.assigned_by} · {inv.assigned_at ? new Date(inv.assigned_at).toLocaleString() : ""}
        </p>
      )}
      {!decided && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <Select value={assignee} onValueChange={setAssignee}>
              <SelectTrigger data-testid="assignee-select" className="text-sm">
                <SelectValue placeholder="Assign to a teammate…" />
              </SelectTrigger>
              <SelectContent>
                {users.map((u) => (
                  <SelectItem key={u} value={u} className="text-sm">
                    {u}{u === user ? " (me)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button data-testid="assign-btn" onClick={() => run(assignee)} disabled={busy || !assignee}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
          <Input
            data-testid="assign-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note — posted to the thread with an @mention"
            className="text-sm"
          />
        </div>
      )}
    </div>
  );
};
